import { isPlatformBrowser } from '@angular/common';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withFetch,
  withInterceptors,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  APP_BOOTSTRAP_LISTENER,
  APP_INITIALIZER,
  ApplicationRef,
  inject,
  Injector,
  NgModule,
  NgZone,
  PLATFORM_ID,
} from '@angular/core';
import { AngularFireModule, FIREBASE_OPTIONS } from '@angular/fire/compat';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import {
  BrowserModule,
  Meta,
  provideClientHydration,
  withEventReplay,
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter, RouterOutlet, withInMemoryScrolling } from '@angular/router';
import { provideTranslateService } from '@ngx-translate/core';
import { acceptLanguageInterceptor } from '@suvo/frontend-framework/standalone/i18n';
import { provideSandboxedWebComponentIframeSrc } from '@suvo/frontend-framework/standalone/web-components/sandboxed/sandboxed-web-component-iframe-src.provider';
import { CommonComponentsModule } from '@suvo/frontend-framework/SUVO-CORE-CLIENT-LIB/projects/suvo-core-client-lib/src/lib/features/common-components/common-components.module';
import { CustomComponentRPCModule } from '@suvo/frontend-framework/SUVO-BI-CLIENT-LIB/projects/suvo-bi-client-lib/src/lib/features/custom-components/rpc/custom-component-rpc.module';
import { CurrentTenantService } from '@suvo/frontend-framework/SUVO-USERS-CLIENT-LIB/projects/suvo-users-client-lib/src/lib/features/tenant/services/current-tenant.service';
import { GlobalElementsService } from '@suvo/frontend-framework/SUVO-USERS-CLIENT-LIB/projects/suvo-users-client-lib/src/lib/features/tenant/services/global-elements.service';
import { SuvoUsersClientLibModule } from '@suvo/frontend-framework/SUVO-USERS-CLIENT-LIB/projects/suvo-users-client-lib/src/lib/suvo-users-client-lib.module';
import { NgcCookieConsentModule } from 'ngx-cookieconsent';
import { MarkdownModule } from 'ngx-markdown';
import { NgxStripeModule, StripeService } from 'ngx-stripe';
import { StripeConstructorOptions } from '@stripe/stripe-js';
import { filter, take } from 'rxjs';
import { ACTIVE_SHOP_DATA } from '@informally/frontend/active-shop';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import routes from './app.routes';
import { PrivateModule } from './features/private/private.module';
import { PublicModule } from './features/public/public.module';
import { GoogleAnalyticsService } from './features/services/google-analytics/google-analytics.service';
import { ThemingService } from './features/services/theming/theming.service';
import { SubscriptionInterceptor } from './shared/interceptor/subscription.interceptor';

@NgModule({
  declarations: [AppComponent],
  imports: [
    AngularFireModule,
    NgxStripeModule.forRoot(''),
    BrowserModule,
    BrowserAnimationsModule,
    CommonComponentsModule,
    RouterOutlet,
    CustomComponentRPCModule,

    SuvoUsersClientLibModule.forRoot(environment),

    // TODO: need to get rid of these and ensure lazy-loaded!
    PrivateModule.forRoot(),
    PublicModule,

    NgcCookieConsentModule.forRoot({
      enabled: false,
      cookie: {
        domain: environment.publicUrl,
        secure: false,
        expiryDays: 365,
        path: 'emt-consent',
        name: 'emt-consent',
      },
      content: {
        href: '/privacy-policy',
      },
      palette: {
        popup: {
          background: '#003a69',
        },
        button: {
          background: '#ffffff',
        },
      },
      revokable: false,
      theme: 'edgeless',
      position: 'bottom-right',
      law: {
        countryCode: 'test',
        regionalLaw: false,
      },
    }),
    MarkdownModule.forRoot(),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [Injector],
      useFactory: (injector: Injector) => async () => {
        const shopData = await injector.get(ACTIVE_SHOP_DATA)();
        const isBrowser = isPlatformBrowser(injector.get(PLATFORM_ID));

        console.log({ shopData });

        // TODO: remove this configuration once code is migrated away from using these
        const tenantService = injector.get(CurrentTenantService);
        tenantService.currentFirebaseTenantId = shopData.firebaseTenantId;
        await tenantService.init(
          `${environment.usersServiceApi}tenant/${shopData.firebaseTenantId}`,
        );

        const tenant = tenantService.currentTenant();
        const stripePublishableKey = tenant?.stripePublishableKey;
        const stripeConnectionType = tenant?.stripeConnectionType;
        const stripeAccountId = tenant?.stripeAccountId;

        const stripeOptions: StripeConstructorOptions | undefined =
          stripeConnectionType === 'express' && stripeAccountId
            ? { stripeAccount: stripeAccountId }
            : undefined;

        if (isBrowser && stripePublishableKey) {
          const stripeService = injector.get(StripeService);
          if (stripeService.key !== stripePublishableKey) {
            stripeService.changeKey(stripePublishableKey, stripeOptions);
          }
        }

        (await injector.get(AngularFireAuth).app).auth().tenantId = shopData.firebaseTenantId;

        // Handle redirect or disabled configurations
        // TODO: we could handle this in the server with proper HTTP redirects now
        const redirectURL = isBrowser
          ? shopData.redirectsTo || (shopData.disabled ? 'https://informally.io' : false)
          : false;

        if (redirectURL) {
          window.location.assign(redirectURL);
          throw Error(`Redirecting to ${redirectURL}...`); // Stop initialising
        }

        injector.get(ThemingService).init();

        // Google Analytics
        if (isBrowser && shopData?.tenant?.trackingId) {
          injector.get(GoogleAnalyticsService).init(shopData.tenant.trackingId);
        }

        injector.get(GlobalElementsService).init(shopData.tenant._id);
      },
    },
    {
      provide: APP_BOOTSTRAP_LISTENER,
      multi: true,
      useFactory: () => {
        const appRef = inject(ApplicationRef);
        const zone = inject(NgZone);
        const pid = inject(PLATFORM_ID);
        const injector = inject(Injector);
        return () => {
          if (!isPlatformBrowser(pid)) return;

          appRef.isStable.pipe(filter(Boolean), take(1)).subscribe(async () => {
            const shopData = await injector.get(ACTIVE_SHOP_DATA)();
            const tenantService = injector.get(CurrentTenantService);
            const tenant = tenantService.currentTenant();
            const stripePublishableKey = tenant?.stripePublishableKey;
            const stripeConnectionType = tenant?.stripeConnectionType;
            const stripeAccountId = tenant?.stripeAccountId;
            const stripeOptions: StripeConstructorOptions | undefined =
              stripeConnectionType === 'express' && stripeAccountId
                ? { stripeAccount: stripeAccountId }
                : undefined;

            // Stripe
            if (stripePublishableKey) {
              zone.runOutsideAngular(() => {
                const stripeService = injector.get(StripeService);
                if (stripeService.key !== stripePublishableKey) {
                  stripeService.changeKey(stripePublishableKey, stripeOptions);
                }
              });
            }

          });
        };
      },
    },
    {
      provide: FIREBASE_OPTIONS,
      useValue: {
        authDomain: environment.firebaseAuthDomain,
        apiKey: environment.firebaseApiKey,
      },
    },
    {
      provide: HTTP_INTERCEPTORS,
      multi: true,
      useClass: SubscriptionInterceptor,
    },
    Meta,
    provideClientHydration(withEventReplay()),
    provideSandboxedWebComponentIframeSrc('/assets/sandboxed-web-component-iframe.html'),
    provideTranslateService({ defaultLanguage: 'en' }),
    provideHttpClient(
      withFetch(),
      withInterceptorsFromDi(),
      withInterceptors([acceptLanguageInterceptor]),
    ),
    provideRouter(
      routes,
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'top',
      }),
    ),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
